import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Navbar } from "@/components/Navbar";

import { Package, Fingerprint, Check, AlertTriangle, ShieldCheck } from "lucide-react";
import { checkPasswordBreach, formatBreachCount } from "@/lib/passwordBreachCheck";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { getRoleBasedRedirect } from "@/hooks/useSessionManager";

import HCaptcha from "@hcaptcha/react-hcaptcha";
const HCAPTCHA_SITE_KEY = "082a375a-fede-433a-8c56-a3c29ba8f3de";

const PLAN_DETAILS: Record<string, { name: string; price: string; description: string }> = {
  monthly: { name: "Monthly", price: "$14.99/mo", description: "1 pickup per month" },
  "bi-weekly": { name: "Bi-Weekly", price: "$24.99/mo", description: "2 pickups per month" },
  "one-time": { name: "One-Time", price: "$14.99", description: "Single pickup" },
};

const Auth = () => {
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get("plan");
  const selectedPriceId = searchParams.get("priceId");
  const tabParam = searchParams.get("tab");
  
  const defaultTab = selectedPlan ? "signup" : (tabParam || "signin");
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [useBiometric, setUseBiometric] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // Default to true for persistent sessions
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const [passwordBreachStatus, setPasswordBreachStatus] = useState<'unchecked' | 'checking' | 'safe' | 'breached'>('unchecked');
  const [breachCount, setBreachCount] = useState<number>(0);
  
  // Password recovery state
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { signIn, signUp, user } = useAuth();

  // Check for password recovery flow on mount
  useEffect(() => {
    const checkRecoveryMode = async () => {
      // Check URL hash for recovery token (Supabase adds it as hash fragment)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get("type");
      const accessToken = hashParams.get("access_token");
      
      if (type === "recovery" && accessToken) {
        setIsRecoveryMode(true);
        // Set the session with the recovery token
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: hashParams.get("refresh_token") || "",
          });
          if (error) {
            console.error("Recovery session error:", error);
            toast({
              title: "Recovery link expired",
              description: "Please request a new password reset link.",
              variant: "destructive",
            });
            setIsRecoveryMode(false);
          }
        } catch (err) {
          console.error("Recovery error:", err);
        }
      }
    };
    
    checkRecoveryMode();
  }, [toast]);

  // Auto-redirect if already authenticated (but not in recovery mode)
  useEffect(() => {
    const checkExistingSession = async () => {
      if (user && !isRecoveryMode) {
        const redirectPath = await getRoleBasedRedirect();
        navigate(redirectPath, { replace: true });
      }
    };
    
    checkExistingSession();
  }, [user, navigate, isRecoveryMode]);

  // Handle password reset submission
  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters.",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      
      if (error) throw error;
      
      toast({
        title: "Password updated!",
        description: "Your password has been successfully changed. Please sign in.",
      });
      
      // Clear recovery mode and redirect to sign in
      setIsRecoveryMode(false);
      setNewPassword("");
      setConfirmNewPassword("");
      // Clear the hash from URL
      window.history.replaceState(null, "", window.location.pathname);
      setActiveTab("signin");
    } catch (error: any) {
      toast({
        title: "Error updating password",
        description: error.message || "Please try again or request a new reset link.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyCaptcha = async (token: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-captcha', {
        body: { token }
      });
      
      if (error) {
        console.error('Captcha verification error:', error);
        return false;
      }
      
      return data?.valid === true;
    } catch (error) {
      console.error('Captcha verification failed:', error);
      return false;
    }
  };

  const resetCaptcha = () => {
    setCaptchaToken(null);
    captchaRef.current?.resetCaptcha();
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Validation Error",
        description: "Please enter email and password",
        variant: "destructive",
      });
      return;
    }

    if (!captchaToken) {
      toast({
        title: "Verification Required",
        description: "Please complete the captcha verification",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Verify captcha server-side
      const isValidCaptcha = await verifyCaptcha(captchaToken);
      if (!isValidCaptcha) {
        resetCaptcha();
        toast({
          title: "Verification Failed",
          description: "Captcha verification failed. Please try again.",
          variant: "destructive",
        });
        return;
      }

      await signIn(email, password);
      
      toast({
        title: "Welcome back!",
        description: "You've successfully signed in.",
      });
      
      // Get role-based redirect
      const redirectPath = await getRoleBasedRedirect();
      navigate(redirectPath);
    } catch (error: any) {
      resetCaptcha();
      toast({
        title: "Error signing in",
        description: error.message || "Please check your credentials and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast({
        title: "Terms Required",
        description: "You must agree to the Terms and Conditions to create an account.",
        variant: "destructive",
      });
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter your first and last name",
        variant: "destructive",
      });
      return;
    }

    // Validate phone number (basic US format)
    const phoneRegex = /^[\d\s\-\(\)]{10,}$/;
    if (!phoneNumber.trim() || !phoneRegex.test(phoneNumber.replace(/\D/g, '')) || phoneNumber.replace(/\D/g, '').length < 10) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid phone number",
        variant: "destructive",
      });
      return;
    }

    // Validate address fields
    if (!street.trim() || !city.trim() || !state || !zip.trim()) {
      toast({
        title: "Validation Error",
        description: "Please complete your full address",
        variant: "destructive",
      });
      return;
    }

    // Validate ZIP code (5 digits)
    const zipRegex = /^\d{5}$/;
    if (!zipRegex.test(zip.trim())) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid 5-digit ZIP code",
        variant: "destructive",
      });
      return;
    }
    
    if (!email || !password || password.length < 8) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid email and password (min 8 characters)",
        variant: "destructive",
      });
      return;
    }

    if (!captchaToken) {
      toast({
        title: "Verification Required",
        description: "Please complete the captcha verification",
        variant: "destructive",
      });
      return;
    }

    // Check if password is breached and warn user
    if (passwordBreachStatus === 'breached') {
      const confirmed = window.confirm(
        `⚠️ Security Warning\n\nThis password has been found in ${formatBreachCount(breachCount)} data breaches. Using a compromised password puts your account at risk.\n\nAre you sure you want to continue with this password?`
      );
      if (!confirmed) {
        return;
      }
    }
    
    setLoading(true);

    try {
      // Verify captcha server-side
      const isValidCaptcha = await verifyCaptcha(captchaToken);
      if (!isValidCaptcha) {
        resetCaptcha();
        toast({
          title: "Verification Failed",
          description: "Captcha verification failed. Please try again.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
    
      await signUp(email, password);

      // Update profile with all required fields
      const { data: { user: newUser } } = await supabase.auth.getUser();
      if (newUser) {
        await supabase
          .from("profiles")
          .update({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            phone_number: phoneNumber.trim(),
            street: street.trim(),
            city: city.trim(),
            state: state,
            zip: zip.trim(),
          })
          .eq("id", newUser.id);
      }
      
      toast({
        title: "Account created!",
        description: "Please complete your profile to continue.",
      });
      
      // Redirect to settings with setup flag and selected plan info
      const setupParams = new URLSearchParams();
      setupParams.set("setup", "true");
      if (selectedPlan) setupParams.set("plan", selectedPlan);
      if (selectedPriceId) setupParams.set("priceId", selectedPriceId);
      
      navigate(`/settings?${setupParams.toString()}`);
    } catch (error: any) {
      resetCaptcha();
      let errorMessage = error.message || "Please try again.";
      
      if (error.message?.includes("already registered")) {
        errorMessage = "This email is already registered. Please sign in instead.";
      } else if (error.message?.includes("upstream") || error.message?.includes("503") || error.message?.includes("connection") || !error.message || error.message === "0") {
        errorMessage = "Our service is temporarily unavailable. Please try again in a few minutes.";
      }
      
      toast({
        title: "Error creating account",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;

      toast({
        title: "Password reset email sent",
        description: "Check your email for the password reset link.",
      });
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error: any) {
      toast({
        title: "Error sending reset email",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  // If in recovery mode, show password reset form
  if (isRecoveryMode) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <Navbar />
        
        <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 md:py-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <Card className="w-full max-w-md shadow-medium">
            <CardHeader className="text-center px-4 sm:px-6">
              <div className="flex justify-center mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 bg-primary/10 rounded-full">
                  <Package className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                </div>
              </div>
              <CardTitle className="text-xl sm:text-2xl">Reset Your Password</CardTitle>
              <CardDescription className="text-sm sm:text-base">
                Enter your new password below
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                  <Input
                    id="confirm-new-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Updating..." : "Update Password"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setIsRecoveryMode(false);
                    window.history.replaceState(null, "", window.location.pathname);
                  }}
                >
                  Back to Sign In
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <Navbar />
      
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 md:py-16 flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Card className="w-full max-w-md shadow-medium">
          <CardHeader className="text-center px-4 sm:px-6">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="p-2 sm:p-3 bg-primary/10 rounded-full">
                <Package className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-xl sm:text-2xl">
              {activeTab === "signup" ? "Complete Your Signup" : "Welcome to Returnify"}
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">
              {activeTab === "signup"
                ? "Create your account to continue"
                : "Sign in or create a new account"
              }
            </CardDescription>
            
            {/* Show selected plan badge - only on signup tab, not clickable */}
            {activeTab === "signup" && selectedPlan && PLAN_DETAILS[selectedPlan] && (
              <div className="mt-3 sm:mt-4 p-3 rounded-lg border border-primary/30 bg-primary/5">
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="font-medium">{PLAN_DETAILS[selectedPlan].name} Plan</span>
                  <span className="text-muted-foreground hidden sm:inline">•</span>
                  <span className="text-primary font-semibold">{PLAN_DETAILS[selectedPlan].price}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {PLAN_DETAILS[selectedPlan].description}
                </p>
              </div>
            )}
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              
              <TabsContent value="signin">
                {showForgotPassword ? (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Email</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="you@example.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? "Sending..." : "Send Reset Link"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => setShowForgotPassword(false)}
                    >
                      Back to Sign In
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <Input
                        id="signin-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                      <label className="flex items-center space-x-2 cursor-pointer min-h-[44px] sm:min-h-0">
                        <input
                          type="checkbox"
                          id="remember-me"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="rounded w-5 h-5 sm:w-4 sm:h-4"
                        />
                        <span className="text-sm">Remember me</span>
                      </label>
                      
                      <label className="flex items-center space-x-2 cursor-pointer min-h-[44px] sm:min-h-0">
                        <input
                          type="checkbox"
                          id="use-biometric"
                          checked={useBiometric}
                          onChange={(e) => setUseBiometric(e.target.checked)}
                          className="rounded w-5 h-5 sm:w-4 sm:h-4"
                        />
                        <span className="flex items-center text-sm">
                          <Fingerprint className="w-4 h-4 mr-1" />
                          Biometric
                        </span>
                      </label>
                    </div>

                    <div className="flex justify-center">
                      <HCaptcha
                        ref={captchaRef}
                        sitekey={HCAPTCHA_SITE_KEY}
                        onVerify={setCaptchaToken}
                        onExpire={() => setCaptchaToken(null)}
                      />
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={loading || !captchaToken}>
                      {loading ? "Signing in..." : "Sign In"}
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      className="w-full"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot Password?
                    </Button>
                  </form>
                )}
              </TabsContent>
              
              <TabsContent value="signup">
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="signup-firstname">First Name</Label>
                      <Input
                        id="signup-firstname"
                        type="text"
                        placeholder="John"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        maxLength={50}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-lastname">Last Name</Label>
                      <Input
                        id="signup-lastname"
                        type="text"
                        placeholder="Doe"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        maxLength={50}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-phone">Phone Number</Label>
                    <Input
                      id="signup-phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                      maxLength={20}
                    />
                  </div>
                  
                  <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
                    <p className="text-sm font-medium">Home Address</p>
                    <div className="space-y-2">
                      <Label htmlFor="signup-street">Street Address</Label>
                      <Input
                        id="signup-street"
                        type="text"
                        placeholder="123 Main St"
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        required
                        maxLength={100}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="signup-city">City</Label>
                        <Input
                          id="signup-city"
                          type="text"
                          placeholder="City"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          required
                          maxLength={50}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-state">State</Label>
                        <select
                          id="signup-state"
                          value={state}
                          onChange={(e) => setState(e.target.value)}
                          required
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <option value="">Select</option>
                          <option value="AL">AL</option>
                          <option value="AK">AK</option>
                          <option value="AZ">AZ</option>
                          <option value="AR">AR</option>
                          <option value="CA">CA</option>
                          <option value="CO">CO</option>
                          <option value="CT">CT</option>
                          <option value="DE">DE</option>
                          <option value="FL">FL</option>
                          <option value="GA">GA</option>
                          <option value="HI">HI</option>
                          <option value="ID">ID</option>
                          <option value="IL">IL</option>
                          <option value="IN">IN</option>
                          <option value="IA">IA</option>
                          <option value="KS">KS</option>
                          <option value="KY">KY</option>
                          <option value="LA">LA</option>
                          <option value="ME">ME</option>
                          <option value="MD">MD</option>
                          <option value="MA">MA</option>
                          <option value="MI">MI</option>
                          <option value="MN">MN</option>
                          <option value="MS">MS</option>
                          <option value="MO">MO</option>
                          <option value="MT">MT</option>
                          <option value="NE">NE</option>
                          <option value="NV">NV</option>
                          <option value="NH">NH</option>
                          <option value="NJ">NJ</option>
                          <option value="NM">NM</option>
                          <option value="NY">NY</option>
                          <option value="NC">NC</option>
                          <option value="ND">ND</option>
                          <option value="OH">OH</option>
                          <option value="OK">OK</option>
                          <option value="OR">OR</option>
                          <option value="PA">PA</option>
                          <option value="RI">RI</option>
                          <option value="SC">SC</option>
                          <option value="SD">SD</option>
                          <option value="TN">TN</option>
                          <option value="TX">TX</option>
                          <option value="UT">UT</option>
                          <option value="VT">VT</option>
                          <option value="VA">VA</option>
                          <option value="WA">WA</option>
                          <option value="WV">WV</option>
                          <option value="WI">WI</option>
                          <option value="WY">WY</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-zip">ZIP Code</Label>
                      <Input
                        id="signup-zip"
                        type="text"
                        placeholder="12345"
                        value={zip}
                        onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        required
                        maxLength={5}
                        pattern="\d{5}"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={async () => {
                        if (password.length >= 8) {
                          setPasswordBreachStatus('checking');
                          const result = await checkPasswordBreach(password);
                          if (result.isBreached) {
                            setPasswordBreachStatus('breached');
                            setBreachCount(result.count);
                          } else {
                            setPasswordBreachStatus('safe');
                          }
                        }
                      }}
                      required
                      minLength={8}
                    />
                    <p className="text-xs text-muted-foreground">
                      Must be at least 8 characters with uppercase, lowercase, and number
                    </p>
                    
                    {/* Password breach status indicator */}
                    {passwordBreachStatus === 'checking' && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <span className="animate-pulse">●</span> Checking password security...
                      </p>
                    )}
                    {passwordBreachStatus === 'safe' && (
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Password not found in known breaches
                      </p>
                    )}
                    {passwordBreachStatus === 'breached' && (
                      <div className="p-2 rounded-md bg-destructive/10 border border-destructive/30">
                        <p className="text-xs text-destructive flex items-center gap-1 font-medium">
                          <AlertTriangle className="w-3 h-3" /> 
                          Password found in {formatBreachCount(breachCount)} data breaches!
                        </p>
                        <p className="text-xs text-destructive/80 mt-1">
                          This password has been exposed. We strongly recommend choosing a different one.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <label className="flex items-center space-x-2 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      id="use-biometric-signup"
                      checked={useBiometric}
                      onChange={(e) => setUseBiometric(e.target.checked)}
                      className="rounded w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0"
                    />
                    <span className="flex items-center text-sm">
                      <Fingerprint className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="hidden sm:inline">Enable biometric authentication (Face ID/Touch ID)</span>
                      <span className="sm:hidden">Enable Face ID/Touch ID</span>
                    </span>
                  </label>

                  <label className="flex items-start space-x-2 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      id="agree-terms"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="rounded w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5"
                    />
                    <span className="text-sm">
                      By creating an account, you agree to the{" "}
                      <Link to="/terms" className="text-primary hover:underline font-medium">
                        Terms and Conditions
                      </Link>
                      .
                    </span>
                  </label>

                  <div className="flex justify-center">
                    <HCaptcha
                      ref={captchaRef}
                      sitekey={HCAPTCHA_SITE_KEY}
                      onVerify={setCaptchaToken}
                      onExpire={() => setCaptchaToken(null)}
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={loading || !agreedToTerms || !captchaToken}>
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
